'use client';

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { AppWindow, Plus, Search, X } from 'lucide-react';
import { BotIcon } from '@/components/icons/bot-icon';
import { Avatar } from '@/components/avatar';
import { cn } from '@/lib/utils';

/**
 * "Attach app or agent" — the promo control used by the post composer, the
 * inline post editor, and the channel surfaces. Picking an app or agent
 * binds the target (`attached_content_id` / `attached_agent_id` on posts,
 * `primary_content_id` / `primary_agent_id` on channels), which renders the
 * Open (app) / Chat (agent) CTA.
 *
 * Sources:
 *  - "mine" apps  = the user's own widgets (`/api/content?type=widget&owner=me`),
 *    the same list as Apps → My Apps. (NOT marketplace my-listings — catalog
 *    apps are platform-owned channels, which made this picker come up empty.)
 *  - "mine" agents = `/api/users/me/agents`.
 *  - scope="all" adds a search over the whole marketplace
 *    (`/api/marketplace/channels?root=…&q=…`) so any listed app/agent can be
 *    promoted. Channel binding stays owner-only server-side, so channel
 *    surfaces should keep scope="mine".
 */

export type AttachedEntity = {
  kind: 'app' | 'agent';
  id: string;
  name: string;
};

type Row = {
  id: string;
  kind: 'app' | 'agent';
  name: string;
  sub: string;
  logoUrl: string | null;
  /** For agents, the avatar seed (username). */
  seed?: string;
};

export function AttachEntityPicker({
  value,
  onChange,
  scope = 'mine',
}: {
  value: AttachedEntity | null;
  onChange: (next: AttachedEntity | null) => void;
  /**
   * 'mine' → only entities the user owns (channel binding requires
   * ownership). 'all' → adds marketplace-wide search (post promos can
   * point at any listed app/agent).
   */
  scope?: 'mine' | 'all';
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'app' | 'agent'>('app');
  const [query, setQuery] = useState('');
  const [mine, setMine] = useState<Row[]>([]);
  const [results, setResults] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(false);

  // My apps (own widgets) + my agents — loaded when the picker opens.
  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    setLoading(true);
    Promise.all([
      fetch('/api/content?type=widget&owner=me&limit=50&page=1&sort=newest', { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d): Row[] =>
          ((d?.items ?? []) as { id: string; displayName: string }[]).map((w) => ({
            id: w.id,
            kind: 'app' as const,
            name: w.displayName,
            sub: 'your app',
            logoUrl: null,
          })),
        )
        .catch(() => [] as Row[]),
      fetch('/api/users/me/agents?limit=50', { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d): Row[] =>
          ((d?.items ?? []) as { id: string; username: string; displayName: string | null; avatarUrl: string | null }[]).map(
            (a) => ({
              id: a.id,
              kind: 'agent' as const,
              name: a.displayName || a.username,
              sub: `@${a.username}`,
              logoUrl: a.avatarUrl,
              seed: a.username,
            }),
          ),
        )
        .catch(() => [] as Row[]),
    ]).then(([apps, agents]) => {
      if (ctrl.signal.aborted) return;
      setMine([...apps, ...agents]);
      setLoading(false);
    });
    return () => ctrl.abort();
  }, [open]);

  // Marketplace-wide search (scope="all"): any listed app/agent, debounced.
  useEffect(() => {
    if (!open || scope !== 'all' || !query.trim()) {
      setResults(null);
      return;
    }
    const ctrl = new AbortController();
    const id = window.setTimeout(() => {
      const root = tab === 'app' ? 'apps' : 'agents';
      fetch(`/api/marketplace/channels?root=${root}&q=${encodeURIComponent(query.trim())}`, {
        signal: ctrl.signal,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (ctrl.signal.aborted) return;
          const rows: Row[] = (
            (d?.items ?? []) as {
              name: string;
              slug: string;
              logoUrl: string | null;
              primaryContentId: string | null;
              primaryAgentId: string | null;
            }[]
          )
            .map((c) => {
              const entityId = c.primaryContentId ?? c.primaryAgentId;
              if (!entityId) return null;
              return {
                id: entityId,
                kind: c.primaryContentId ? ('app' as const) : ('agent' as const),
                name: c.name,
                sub: `//${c.slug}`,
                logoUrl: c.logoUrl,
              };
            })
            .filter((r): r is Row => !!r);
          setResults(rows);
        })
        .catch(() => {});
    }, 250);
    return () => {
      window.clearTimeout(id);
      ctrl.abort();
    };
  }, [open, scope, query, tab]);

  if (value) {
    return (
      <div
        className={cn(
          'flex items-center gap-2.5 rounded-lg border px-3 py-2',
          value.kind === 'app'
            ? 'border-primary/30 bg-primary/5'
            : 'border-purple-400/30 bg-purple-400/5',
        )}
      >
        {value.kind === 'app' ? (
          <AppWindow className="h-4 w-4 shrink-0 text-primary" />
        ) : (
          <BotIcon className="h-4 w-4 shrink-0 text-purple-500" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{value.name}</div>
          <div className="text-xs text-muted-foreground">
            {value.kind === 'app'
              ? t('create.attachAppHint')
              : t('create.attachAgentHint')}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={t('create.removeAttachment')}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Plus className="h-4 w-4" />
        {t('create.attachAppOrAgent')}
      </button>
    );
  }

  const source = results ?? mine;
  const rows = source.filter((r) => r.kind === tab);

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
        <button
          type="button"
          onClick={() => setTab('app')}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            tab === 'app' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Apps
        </button>
        <button
          type="button"
          onClick={() => setTab('agent')}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            tab === 'agent' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Agents
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('workspace.canvas.node.close')}
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {scope === 'all' && (
        <div className="relative border-b border-border">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search all ${tab === 'app' ? 'apps' : 'agents'}…`}
            className="h-8 w-full bg-transparent pl-8 pr-2 text-xs outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
      <div className="max-h-48 overflow-y-auto">
        {loading && !rows.length ? (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            {results !== null
              ? 'No matches.'
              : tab === 'app'
                ? t('create.noAppsYet')
                : t('workspace.panels.noAgentsYet')}
          </div>
        ) : (
          rows.map((r) => (
            <button
              key={`${r.kind}:${r.id}`}
              type="button"
              onClick={() => {
                onChange({ kind: r.kind, id: r.id, name: r.name });
                setOpen(false);
                setQuery('');
              }}
              className="flex w-full items-center gap-2.5 border-b border-border/40 px-3 py-2 text-left transition-colors last:border-0 hover:bg-accent/50"
            >
              {r.kind === 'agent' ? (
                <Avatar seed={r.seed ?? r.name} type="agent" src={r.logoUrl ?? undefined} className="h-7 w-7" />
              ) : r.logoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={r.logoUrl} alt="" className="h-7 w-7 rounded-lg object-cover" />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <AppWindow className="h-4 w-4" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{r.name}</span>
                <span className="block truncate font-mono text-[10px] text-muted-foreground">{r.sub}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
